import { ContentType, LocalesValue, sentryLogger } from "@robe/core";
import { XcoreData } from "./../../context/XcoreDataContext";
import { ContentLinkOptions } from ".";
import { isValidElement, ReactNode } from "react";

export const ID_REGEX = /\{id\}|\[id\]/g;
export const SLUG_REGEX = /\{slug\}|\[slug\]/g;

export type ContentLinkContextData = Pick<XcoreData, "core" | "config" | "resolvedDomains"> & {
  contentType: ContentType;
};

/** Resolves routing locale and domain based on provided options and xcore config */
export const resolveRouting = (
  { config, resolvedDomains, core: { currentLocale }, contentType }: ContentLinkContextData,
  options?: ContentLinkOptions
): { contentLocale: string | null; host: string | null } => {
  let contentLocale: string | null = options?.locale ?? null;
  // Fallback to window object host, because domains like it and es are both for en locale so it is impossible to define host from locale for these
  let host: string | null = typeof window !== "undefined" ? window.location.origin : null;
  let configLocaleObject: LocalesValue | null = null;

  // If options locale is specified and is available in config locales use it.
  if (options?.locale) {
    configLocaleObject = options.locale in config.locales ? config.locales[options?.locale] : null;
  } else {
    // If options locale is not available and localized route is defined for current locale use it.
    if (contentType?.routes && currentLocale in contentType.routes) {
      contentLocale = currentLocale;
    } else {
      configLocaleObject = !configLocaleObject && currentLocale in config.locales ? config.locales[currentLocale] : null;

      if (!configLocaleObject) {
        contentLocale = null;
      }

      // Use fallback locale route if route is not available for current locale
      if (configLocaleObject?.routing && configLocaleObject?.routing in config.locales) {
        contentLocale = configLocaleObject.routing;
      }
    }
  }

  // Get domain by locale
  const localeDomainResolved =
    Object.entries(resolvedDomains).find(([_, locale]) => locale === (options?.locale ?? currentLocale))?.[0] ?? null;

  // Respect options locale if defined
  if ((!host || options?.locale) && localeDomainResolved) {
    if (localeDomainResolved === "/") {
      // If localized paths routing is used and resolved domain is default locale domain return empty string because resolved paths already begin with slash
      host = "";
    } else {
      const domainWithoutTrailingSlash = localeDomainResolved?.endsWith("/")
        ? // Remove trailing slash
        localeDomainResolved.slice(0, -1)
        : localeDomainResolved;

      // Only add protocol if localized domains routing is used instead of locales paths routing (local dev setup case)
      host = domainWithoutTrailingSlash.startsWith("/") ? domainWithoutTrailingSlash : `https://${domainWithoutTrailingSlash}`;
    }
  }

  return { contentLocale, host };
};

export const logLinkError = (error: string, ctx?: object) => {
  sentryLogger({
    error: new Error(error),
    message: "An error occurred in <Link> or useLink",
    debugBlock: "link",
    ...(ctx && {
      context: ctx,
    }),
  });
};

/**
 * If string is valid url return its path. If not return the string url.
 */
export const resolvePathOrUrl = (url: string) => {
  try {
    const parsedUrl = new URL(url);
    return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
  } catch (error) {
    return url;
  }
};

export const containsAnchor = (node: ReactNode): boolean => {
  if (Array.isArray(node)) {
    return node.some(containsAnchor);
  }

  if (isValidElement(node)) {
    if (node.type === "a") return true;
    return containsAnchor(node.props.children);
  }

  return false;
};
